// Page-access permission tokens (user.AccessList) — shared source of truth.
//
// Canonical spelling is lowercase + SINGULAR, matching the route guard
// (middleware/block_pages.global.ts) and the legacy FoodWeb convention:
//   pos, report, menu, expense, stock, setting
//
// An early build of the SPA user editor (settings/Tab2Users.vue) mistakenly wrote
// the PLURAL forms `expenses` / `settings`. Those tokens don't match the guard, so
// they could neither grant nor revoke access correctly — a user carrying the legacy
// `expense` token stayed accessible because the editor only ever touched `expenses`.
// We normalize every read/write back to canonical here so old and new data agree.

export const CANONICAL_ACCESS_KEYS = [
  "pos",
  "report",
  "menu",
  "expense",
  "stock",
  "setting",
] as const

// Historical / mistaken spellings → canonical token.
const ACCESS_ALIASES: Record<string, string> = {
  expenses: "expense",
  settings: "setting",
}

/** Fold a single raw token to its canonical spelling (trim + lowercase + alias). */
export function canonicalAccessToken(token: unknown): string {
  const t = String(token ?? "").trim().toLowerCase()
  return ACCESS_ALIASES[t] || t
}

/**
 * Parse a raw AccessList (comma-string or array) into a deduped array of canonical
 * tokens. Unknown tokens (e.g. `all`, `edit-order`) are preserved as-is (lowercased)
 * so we never silently drop a permission we don't recognize.
 */
export function parseAccessListCanonical(raw: unknown): string[] {
  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(",")
      : []
  const out: string[] = []
  for (const item of arr) {
    const t = canonicalAccessToken(item)
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/** Normalize a raw AccessList back into a canonical, deduped comma-joined string. */
export function normalizeAccessList(raw: unknown): string {
  return parseAccessListCanonical(raw).join(",")
}
